import React, { useState, useEffect } from 'react';

const API_BASE = 'https://uknr3r8og3.execute-api.us-east-2.amazonaws.com/api/v1';

const App = () => {
  const [page, setPage] = useState('landing');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [regStep, setRegStep] = useState(1);
  const [regEmail, setRegEmail] = useState('');

  useEffect(() => { if (token) checkUserStatus(); }, [token]);

  const api = {
    async post(endpoint, data, authToken = null) {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${API_BASE}${endpoint}`, { method: 'POST', headers, body: JSON.stringify(data) });
      return res;
    },
    async get(endpoint, authToken) {
      return fetch(`${API_BASE}${endpoint}`, { headers: { 'Authorization': `Bearer ${authToken}` } });
    }
  };

  const checkUserStatus = async () => {
    try {
      const res = await api.get('/profile', token);
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        const prefsRes = await api.get('/preferences', token);
        if (prefsRes.ok) {
          const prefsData = await prefsRes.json();
          if (prefsData.has_preferences) { setPage('matches'); loadMatches(); }
          else { setPage('preferences'); }
        }
      } else if (res.status === 404) { setPage('create-profile'); }
      else { logout(); }
    } catch (e) { console.error(e); }
  };

  const loadMatches = async () => {
    try {
      const res = await api.get('/matches', token);
      if (res.ok) { const data = await res.json(); setMatches(data.matches || []); }
    } catch (e) { console.error(e); }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null); setProfile(null); setMatches([]);
    setPage('landing');
  };

  const showError = (msg) => { setError(msg); setTimeout(() => setError(''), 5000); };
  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 5000); };

  // Landing Page
  const LandingPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <header className="flex justify-between items-center mb-12">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center">
              <span className="text-indigo-600 font-bold text-lg">A</span>
            </div>
            <span className="text-xl font-bold text-white">Amelamatch</span>
          </div>
          <div className="space-x-3">
            <button onClick={() => setPage('login')} className="px-5 py-2 text-white hover:bg-white/10 rounded-lg">Login</button>
            <button onClick={() => { setRegStep(1); setPage('register'); }} className="px-5 py-2 bg-white text-indigo-600 rounded-lg font-semibold">Register</button>
          </div>
        </header>
        <div className="text-center text-white py-16">
          <h1 className="text-4xl font-bold mb-4">Find Your Perfect <span className="text-yellow-300">AMELA</span> Match</h1>
          <p className="text-lg text-indigo-100 mb-6">AI-powered matrimony for Academics, Medical, Engineering, Legal & Accounting professionals.</p>
          <button onClick={() => { setRegStep(1); setPage('register'); }} className="px-8 py-3 bg-white text-indigo-600 rounded-xl font-semibold text-lg hover:bg-indigo-50">Start Your Journey →</button>
        </div>
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {[{s:'1',t:'Create Profile',c:'bg-white text-indigo-600'},{s:'2',t:'Set Priorities',c:'bg-gray-300 text-gray-700'},{s:'3',t:'Get Matches',c:'bg-yellow-400 text-yellow-800'},{s:'4',t:'Connect',c:'bg-green-400 text-green-800'}].map((x,i) => (
            <div key={i} className="bg-white/10 rounded-xl p-4 text-center">
              <div className={`w-10 h-10 ${x.c} rounded-full flex items-center justify-center font-bold mx-auto mb-2`}>{x.s}</div>
              <h3 className="text-white font-semibold">{x.t}</h3>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Register Page
  const RegisterPage = () => {
    const [form, setForm] = useState({ email: '', phone: '', password: '', confirmPassword: '' });
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRegister = async (e) => {
      e.preventDefault();
      if (form.password !== form.confirmPassword) { showError('Passwords do not match'); return; }
      setLoading(true);
      try {
        const res = await api.post('/auth/register', { email: form.email, phone: form.phone, password: form.password });
        const data = await res.json();
        if (res.ok) { setRegEmail(form.email); setRegStep(2); showSuccess('OTP sent to your email!'); }
        else { showError(data.detail || 'Registration failed'); }
      } catch (e) { showError('Network error'); }
      setLoading(false);
    };

    const handleVerifyOtp = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/auth/verify-otp?email=${encodeURIComponent(regEmail)}&otp=${otp}`, { method: 'POST' });
        const data = await res.json();
        if (res.ok) { showSuccess('Email verified! Please login.'); setRegStep(1); setPage('login'); }
        else { showError(data.detail || 'Invalid OTP'); }
      } catch (e) { showError('Network error'); }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <button onClick={() => setPage('landing')} className="text-gray-400 hover:text-gray-600 mb-4">← Back</button>
          {regStep === 1 ? (
            <>
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Create Account</h2>
              <form onSubmit={handleRegister} className="space-y-4">
                <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
                <input type="tel" placeholder="Phone (+919876543210)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
                <input type="password" placeholder="Password (8+ chars)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3 border rounded-xl" minLength={8} required />
                <input type="password" placeholder="Confirm Password" value={form.confirmPassword} onChange={e => setForm({...form, confirmPassword: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
                <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold disabled:opacity-50">{loading ? 'Creating...' : 'Create Account'}</button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Email</h2>
              <p className="text-gray-500 mb-6">Enter the 6-digit code sent to <strong>{regEmail}</strong></p>
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="w-full px-4 py-4 border-2 rounded-xl text-center text-3xl tracking-widest font-bold" maxLength={6} required autoFocus />
                <button type="submit" disabled={loading || otp.length !== 6} className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold disabled:opacity-50">{loading ? 'Verifying...' : 'Verify OTP'}</button>
              </form>
            </>
          )}
          <p className="text-center text-gray-500 mt-6">Already have an account? <button onClick={() => setPage('login')} className="text-indigo-600 font-medium">Login</button></p>
        </div>
      </div>
    );
  };

  // Login Page
  const LoginPage = () => {
    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        const res = await api.post('/auth/login', { username: form.email, password: form.password });
        const data = await res.json();
        if (res.ok) { localStorage.setItem('token', data.access_token); setToken(data.access_token); showSuccess('Login successful!'); }
        else { showError(data.detail || 'Login failed'); }
      } catch (e) { showError('Network error'); }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <button onClick={() => setPage('landing')} className="text-gray-400 hover:text-gray-600 mb-4">← Back</button>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Welcome Back</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
            <input type="password" placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
            <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold disabled:opacity-50">{loading ? 'Logging in...' : 'Login'}</button>
          </form>
          <p className="text-center text-gray-500 mt-6">Don't have an account? <button onClick={() => setPage('register')} className="text-indigo-600 font-medium">Register</button></p>
        </div>
      </div>
    );
  };

  // Matches Page (Dashboard)
  const MatchesPage = () => {
    const [sending, setSending] = useState(null);

    const sendInterest = async (profileId) => {
      setSending(profileId);
      try {
        const res = await fetch(`${API_BASE}/interests?target_profile_id=${profileId}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) { showSuccess('Interest sent!'); setMatches(matches.map(m => m.profile_id === profileId ? {...m, interest_sent: true} : m)); }
      } catch (e) { showError('Failed to send interest'); }
      setSending(null);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">A</span>
              </div>
              <span className="text-xl font-bold text-indigo-600">Amelamatch</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Hi, {profile?.first_name}!</span>
              <button onClick={logout} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg">Logout</button>
            </div>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Your AI-Curated Matches 💕</h1>
          {matches.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl">
              <div className="text-6xl mb-4">💕</div>
              <h3 className="text-xl font-semibold">Finding your matches...</h3>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {matches.map((m, i) => (
                <div key={m.profile_id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className={`bg-gradient-to-r ${i === 0 ? 'from-green-400 to-emerald-500' : 'from-blue-400 to-indigo-500'} px-4 py-2 flex justify-between`}>
                    <span className="text-white font-semibold">{i === 0 ? '🏆 Best Match' : '⭐ Great Match'}</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-white font-bold">{m.compatibility_score}%</span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold">{m.first_name[0]}</div>
                      <div>
                        <h3 className="font-bold text-lg">{m.first_name} {m.last_name_initial}</h3>
                        <p className="text-gray-500">{m.age} yrs • {m.height_cm}cm</p>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-sm">{m.profession_category}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-400">Job</p><p className="font-medium text-sm truncate">{m.profession_title}</p></div>
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-400">City</p><p className="font-medium text-sm">{m.city}</p></div>
                    </div>
                    <button onClick={() => sendInterest(m.profile_id)} disabled={m.interest_sent || sending === m.profile_id}
                      className={`w-full py-2.5 rounded-xl font-medium ${m.interest_sent ? 'bg-green-100 text-green-700' : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'}`}>
                      {m.interest_sent ? '✓ Interest Sent' : sending === m.profile_id ? 'Sending...' : 'Express Interest'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  };

  // Simplified Create Profile & Preferences pages
  const CreateProfilePage = () => {
    const [form, setForm] = useState({ first_name: '', last_name: '', gender: '', date_of_birth: '', profession_category: '', profession_title: '', highest_qualification: '', country: 'India', state: '', city: '', height_cm: '', religion: '' });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        const res = await api.post('/profile', {...form, height_cm: parseInt(form.height_cm)}, token);
        if (res.ok) { showSuccess('Profile created!'); setPage('preferences'); }
        else { const data = await res.json(); showError(data.detail || 'Failed'); }
      } catch (e) { showError('Network error'); }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Create Your Profile</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="First Name" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="px-4 py-3 border rounded-xl" required />
              <input placeholder="Last Name" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="px-4 py-3 border rounded-xl" required />
            </div>
            <select value={form.gender} onChange={e => setForm({...form, gender: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required>
              <option value="">Select Gender</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
            <input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
            <select value={form.profession_category} onChange={e => setForm({...form, profession_category: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required>
              <option value="">Select Profession</option>
              {['ACADEMIC','MEDICAL','ENGINEERING','LEGAL','ACCOUNTING'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input placeholder="Job Title" value={form.profession_title} onChange={e => setForm({...form, profession_title: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
            <input placeholder="Qualification (e.g. MBA, MD)" value={form.highest_qualification} onChange={e => setForm({...form, highest_qualification: e.target.value})} className="w-full px-4 py-3 border rounded-xl" required />
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="State" value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="px-4 py-3 border rounded-xl" required />
              <input placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="px-4 py-3 border rounded-xl" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="number" placeholder="Height (cm)" value={form.height_cm} onChange={e => setForm({...form, height_cm: e.target.value})} className="px-4 py-3 border rounded-xl" required />
              <select value={form.religion} onChange={e => setForm({...form, religion: e.target.value})} className="px-4 py-3 border rounded-xl" required>
                <option value="">Religion</option>
                {['Hindu','Muslim','Christian','Sikh','Jain','Buddhist','Other'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold">{loading ? 'Creating...' : 'Continue →'}</button>
          </form>
        </div>
      </div>
    );
  };

  const PreferencesPage = () => {
    const [priorities, setPriorities] = useState([]);
    const [loading, setLoading] = useState(false);
    const options = ['PROFESSION','EDUCATION','LOCATION','AGE','HEIGHT','RELIGION'];

    const toggle = (p) => {
      if (priorities.includes(p)) setPriorities(priorities.filter(x => x !== p));
      else if (priorities.length < 3) setPriorities([...priorities, p]);
    };

    const handleSubmit = async () => {
      if (priorities.length < 3) { showError('Select 3 priorities'); return; }
      setLoading(true);
      try {
        const res = await api.post('/preferences', { priority_1: priorities[0], priority_2: priorities[1], priority_3: priorities[2], min_age: 21, max_age: 40 }, token);
        if (res.ok) { showSuccess('Preferences saved!'); setPage('matches'); loadMatches(); }
      } catch (e) { showError('Network error'); }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">What Matters Most?</h2>
          <p className="text-gray-500 mb-6">Select your top 3 priorities for AI matching</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {options.map(p => (
              <button key={p} onClick={() => toggle(p)} className={`p-4 rounded-xl border-2 text-left relative ${priorities.includes(p) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                {priorities.includes(p) && <span className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{priorities.indexOf(p) + 1}</span>}
                <span className="font-semibold">{p}</span>
              </button>
            ))}
          </div>
          <button onClick={handleSubmit} disabled={loading || priorities.length < 3} className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold disabled:opacity-50">
            {loading ? 'Saving...' : `Find Matches (${priorities.length}/3)`}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {error && <div className="fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg z-50">{error}</div>}
      {success && <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50">{success}</div>}
      {page === 'landing' && <LandingPage />}
      {page === 'register' && <RegisterPage />}
      {page === 'login' && <LoginPage />}
      {page === 'create-profile' && <CreateProfilePage />}
      {page === 'preferences' && <PreferencesPage />}
      {page === 'matches' && <MatchesPage />}
    </div>
  );
};

export default App;
